if (!customElements.get('aura-gift-edit')) {
  customElements.define('aura-gift-edit', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const nav = this.querySelector('.ae-gift-tabs');
      this.tabs = [...this.querySelectorAll('.ae-gift-tab')];
      this.panels = [...this.querySelectorAll('.ae-gift-panel')];
      if (!nav || !this.tabs.length || this.tabs.length !== this.panels.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      nav.setAttribute('role', 'tablist');
      if ('IntersectionObserver' in window) this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const panel = entry.target.closest('.ae-gift-panel');
          if (entry.isIntersecting && !panel.hidden && !panel.dataset.loadFailed) this.loadMore(panel);
        });
      }, { rootMargin: '650px 0px' });
      this.tabs.forEach((tab, i) => {
        const panel = this.panels[i];
        tab.setAttribute('role', 'tab'); tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel'); panel.tabIndex = 0;
        const more = panel.querySelector('[data-gift-more]');
        if (more) {
          more.addEventListener('click', event => { event.preventDefault(); delete panel.dataset.loadFailed; this.loadMore(panel); }, options);
          this.observer?.observe(more);
        }
        this.updatePanel(panel);
        tab.addEventListener('click', event => { event.preventDefault(); this.activate(i, false, true); }, options);
        tab.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (i + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') target = (i - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.tabs.length - 1;
          if (target !== undefined) { event.preventDefault(); this.activate(target, true, true); }
        }, options);
      });
      const hashIndex = this.panels.findIndex(panel => '#' + panel.id === window.location.hash);
      this.activate(hashIndex >= 0 ? hashIndex : 0);
      this.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel === event.target || panel.contains(event.target));
        if (index >= 0) this.activate(index);
      }, options);
    }
    disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); this.ready = false; }
    activate(index, focus = false, saveHash = false) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1;
        this.panels[i].hidden = !active;
      });
      if (focus) this.tabs[index].focus({ preventScroll: true });
      if (saveHash) {
        history.replaceState(history.state, '', '#' + this.panels[index].id);
        const panel = this.panels[index], nav = this.querySelector('.ae-gift-tabs');
        if (panel.getBoundingClientRect().top < nav.getBoundingClientRect().bottom) panel.scrollIntoView({ block: 'start', behavior: 'instant' });
      }
      window.dispatchEvent(new Event('resize'));
      this.loadIfNear(this.panels[index]);
    }
    updatePanel(panel) {
      const count = panel.querySelectorAll('[data-gift-product-id]').length;
      const text = panel.dataset.nextPage ? this.dataset.shownLabel : this.dataset.totalLabel;
      panel.querySelector('[data-gift-count]').textContent = text.replace('[count]', String(count));
      const more = panel.querySelector('[data-gift-more]');
      if (more) more.hidden = !panel.dataset.nextPage;
    }
    loadIfNear(panel) {
      const more = panel.querySelector('[data-gift-more]');
      if (!this.observer || panel.hidden || !more || more.hidden || panel.dataset.loadFailed) return;
      if (more.getBoundingClientRect().top < window.innerHeight + 650) this.loadMore(panel);
    }
    async loadMore(panel) {
      if (panel.hidden || panel.dataset.loading || !panel.dataset.nextPage) return;
      const button = panel.querySelector('[data-gift-more]'), status = panel.querySelector('[data-gift-status]');
      panel.dataset.loading = 'true'; button.setAttribute('aria-disabled', 'true'); status.textContent = this.dataset.loadingLabel;
      try {
        const url = new URL(panel.dataset.collectionUrl, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid collection URL');
        url.searchParams.set('section_id', 'aura-gift-products'); url.searchParams.set('page', panel.dataset.nextPage);
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { credentials: 'same-origin', signal: this.controller.signal });
        if (!response.ok) throw new Error('Gift collection did not load');
        const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = fragment.querySelector('[data-gift-results]');
        if (!results || results.dataset.collectionHandle !== panel.dataset.collectionHandle) throw new Error('Gift collection response missing');
        const nextPage = results.dataset.nextPage || '';
        if (nextPage && Number(nextPage) <= Number(panel.dataset.nextPage)) throw new Error('Invalid gift pagination');
        const grid = panel.querySelector('.ae-product-grid');
        const existing = new Set([...grid.querySelectorAll('[data-gift-product-id]')].map(card => card.dataset.giftProductId));
        const cards = [...results.querySelectorAll('[data-gift-product-id]')].filter(card => {
          if (existing.has(card.dataset.giftProductId)) return false;
          if (panel.dataset.underFifty === 'true' && Number(card.dataset.giftPriceMax) > 5000) return false;
          existing.add(card.dataset.giftProductId); return true;
        });
        grid.append(...cards); panel.dataset.nextPage = nextPage;
        delete panel.dataset.loadFailed; status.textContent = ''; this.updatePanel(panel);
      } catch (error) {
        if (error.name !== 'AbortError') { panel.dataset.loadFailed = 'true'; status.textContent = this.dataset.errorLabel; }
      } finally { delete panel.dataset.loading; button.removeAttribute('aria-disabled'); }
      if (this.isConnected) requestAnimationFrame(() => this.loadIfNear(panel));
    }
  });
}
